// src/hooks/useWorkflow.ts
import { useState, useEffect, useCallback } from 'react';
import { LaporanMasalah, AvailableTransition, StateTransitionLog } from '../types';
import { laporanService } from '../services/laporan';
import toast from 'react-hot-toast';

interface WorkflowState {
    transitions: AvailableTransition[];
    history: StateTransitionLog[];
    loading: boolean;
    historyLoading: boolean;
    // Convenience flags
    canSubmit: boolean;
    canValidateL1: boolean;
    canRejectL1: boolean;
    canValidateL2: boolean;
    canRejectL2: boolean;
    canQueueRapim: boolean;
    canDecideRapim: boolean;
    canStartPpepp: boolean;
    canComplete: boolean;
    canArchive: boolean;
    canResubmit: boolean;
    // Actions
    changeStatus: (toState: string, notes?: string) => Promise<LaporanMasalah | null>;
    refreshTransitions: () => Promise<void>;
    refreshHistory: () => Promise<void>;
}

export function useWorkflow(laporan: LaporanMasalah | null): WorkflowState {
    const [transitions, setTransitions] = useState<AvailableTransition[]>([]);
    const [history, setHistory] = useState<StateTransitionLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);

    const refreshTransitions = useCallback(async () => {
        if (!laporan?.id) return;
        try {
            setLoading(true);
            const data = await laporanService.getTransitions(laporan.id);
            setTransitions(data);
        } catch (err) {
            console.error('Failed to load transitions:', err);
            setTransitions([]);
        } finally {
            setLoading(false);
        }
    }, [laporan?.id]);

    const refreshHistory = useCallback(async () => {
        if (!laporan?.id) return;
        try {
            setHistoryLoading(true);
            const data = await laporanService.getStateHistory(laporan.id);
            setHistory(data);
        } catch (err) {
            console.error('Failed to load state history:', err);
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [laporan?.id]);

    useEffect(() => {
        refreshTransitions();
        refreshHistory();
    }, [refreshTransitions, refreshHistory]);

    const hasTransition = (state: string) =>
        transitions.some((t) => t.to_state === state);

    const changeStatus = useCallback(
        async (toState: string, notes?: string): Promise<LaporanMasalah | null> => {
            if (!laporan?.id) return null;
            try {
                const result = await laporanService.changeStatus(laporan.id, toState, notes);
                const label = transitions.find((t) => t.to_state === toState)?.label || toState;
                toast.success(`Status berhasil diubah: ${label}`);
                await refreshTransitions();
                await refreshHistory();
                return result;
            } catch (err: any) {
                const msg = err?.response?.data?.message || err?.response?.data?.errors?.status?.[0] || 'Gagal mengubah status';
                toast.error(msg);
                return null;
            }
        },
        [laporan?.id, transitions, refreshTransitions, refreshHistory]
    );

    return {
        transitions,
        history,
        loading,
        historyLoading,
        canSubmit: hasTransition('diajukan'),
        canValidateL1: hasTransition('validasi_l1'),
        canRejectL1: hasTransition('ditolak_l1'),
        canValidateL2: hasTransition('validasi_l2'),
        canRejectL2: hasTransition('ditolak_l2'),
        canQueueRapim: hasTransition('antrean_rapim'),
        canDecideRapim: hasTransition('diputuskan'),
        canStartPpepp: hasTransition('berlangsung'),
        canComplete: hasTransition('selesai'),
        canArchive: hasTransition('diarsipkan'),
        canResubmit: hasTransition('diajukan'),
        changeStatus,
        refreshTransitions,
        refreshHistory,
    };
}
